import * as amplitude from '@amplitude/analytics-browser';
import { Box, Button, WarningIcon } from '@deckee/deck-hand';
import React, { FC, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { initialize } from '../../../services/event-capture/amplitude';
import { ReactSvg, updateApp } from '../../shared/vector-images';

export interface UpdateFromCordovaProps {
  platform: string;
  operatingSystem: string;
  osVersion: string;
}

const UpdateFromCordova: FC<UpdateFromCordovaProps> = ({
  platform,
  operatingSystem,
  osVersion,
}) => {
  let supportedOS = true;

  // Check the osVersion for unsupported version
  let osVersionNumber;
  // make sure osVersion isn't: undefined | null | ‘’ | ‘beta’ | 0 | NaN
  if (osVersion) osVersionNumber = parseInt(osVersion, 10);
  // iOS version < 13 no longer supported
  if (
    platform === 'ios' &&
    operatingSystem !== 'mac' &&
    osVersionNumber &&
    !Number.isNaN(osVersionNumber) &&
    osVersionNumber < 13
  ) {
    supportedOS = false;
  }

  const theme = useTheme();

  const handleUpdateApp = async () => {
    amplitude.track('app-start: update-clicked', { platform });
    let redirectUrl =
      'https://apps.apple.com/au/app/deckee-boating/id1402262696';
    if (platform === 'android') {
      redirectUrl =
        'https://play.google.com/store/apps/details?id=com.deckee.app&hl=en_AU&gl=US&pli=1';
    }
    window.location.href = redirectUrl;
  };

  const handleOpenBrowser = async () => {
    amplitude.track('app-start: open-browser-clicked', { platform });
    window.location.href = 'https://deckee.com/maps';
  };

  useEffect(() => {
    initialize();
    amplitude.track('app-start: update-required', { platform });
  }, [platform]);

  return (
    <Box
      mx={3}
      background="#fff"
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
    >
      <Box
        mb={6}
        flex="1 1 auto"
        maxWidth="400px"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        {supportedOS ? (
          <>
            <ReactSvg
              toDisplay={updateApp()}
              options={{
                fillColor: theme.colors.primary,
              }}
              width={160}
              height={160}
            />

            <Box
              mt={3}
              mb={3}
              fontWeight="bold"
              fontSize={3}
              textAlign="center"
              maxWidth="260px"
            >
              Update your DECKEE app to the latest version
            </Box>
            <Box mb={4} fontSize={2} textAlign="center">
              A faster experience is waiting for you!
            </Box>
            <Button size="large" onClick={handleUpdateApp}>
              Update app
            </Button>
          </>
        ) : (
          <>
            <WarningIcon color={theme.colors.primary} size={90} />

            <Box
              mt={4}
              mb={3}
              fontWeight="bold"
              fontSize={3}
              textAlign="center"
              maxWidth="280px"
            >
              Unfortunately, your device no longer supports DECKEE
            </Box>
            <Box mb={4} fontSize={2} textAlign="center" maxWidth="280px">
              iOS 13 or higher is now required to use the app, and your device
              is running iOS {osVersion}. You can still use DECKEE in your
              mobile web browser.
            </Box>
            <Button size="large" onClick={handleOpenBrowser}>
              Open in browser
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default UpdateFromCordova;
